import { createProductConfig } from 'pricing-pages-lib/utils/productConfigUtils';
import { useGlobalLoading } from '../../../hooks/loadingHooks';
import { usePurchaseableProductsForTerm } from '../../../hooks/useBulkPurchaseOptions';
import { useAppSelector } from '../../../reducers';
import { productLevelForCoreSeatsSelector } from '../../../selectors/ProductConfigsSelectors';
import { coreSeatSkuForProductLevelSelector, isQuantifiableHubSelector, productForSkuIdSelector } from '../../../selectors/ProductsSelectors';
import { useQueryParamTerm } from '../../../utils/urls/appQueryParamUtilsLegacy';
import { productLineToSkuCategoryName } from '../../../v2/constants';
import { useHasHigherProductLevelInProductInventory } from '../../../v2/hooks/inventory/useHighestProductLevel';
import { useHasSkuCategoryNameInBundledProductInventory, useHasSkuCategoryNameInProductInventory } from '../../../v2/hooks/inventory/useProductInventoryLookup';
const useProductOwnershipDetails = ({
  skuId
}) => {
  const isAppReady = useGlobalLoading();
  const isQuantifiableProduct = useAppSelector(state => {
    if (!isAppReady || !skuId) {
      return false;
    }
    return isQuantifiableHubSelector(state, {
      skuId
    });
  });
  const product = useAppSelector(state => {
    if (!isAppReady || !skuId) {
      return null;
    }
    return productForSkuIdSelector(state, {
      skuId
    });
  });
  const productLevel = product === null || product === void 0 ? void 0 : product.productLevel;
  const productLine = product === null || product === void 0 ? void 0 : product.productLine;
  const skuCategoryName = productLine ? productLineToSkuCategoryName[productLine] : undefined;
  const {
    hasSkuCategoryNameInInventory: ownsProductLineViaProducts
  } = useHasSkuCategoryNameInProductInventory({
    skuCategoryName
  });
  const {
    hasSkuCategoryNameInBundledInventory: ownsProductLineViaBundle
  } = useHasSkuCategoryNameInBundledProductInventory({
    skuCategoryName
  });
  const ownsProductLine = ownsProductLineViaProducts || ownsProductLineViaBundle;
  const coreSeatTierOwned = useAppSelector(state => {
    if (!isAppReady) {
      return null;
    }
    return productLevelForCoreSeatsSelector(state, {
      productConfigurations: []
    });
  });
  const {
    hasHigherProductLevel: hasHigherProductLevelInInventory
  } = useHasHigherProductLevelInProductInventory({
    productLevel,
    skuCategoryName
  });

  // Show "Additional Seats start at $X/mo" for all non-quantifiable hubs' cards that are NOT downgrades and NOT in the "/mo/seat" format
  const isDowngrade = isAppReady && productLine && productLevel && hasHigherProductLevelInInventory;
  const isNonQuantifiableProOrEnterpriseProduct = (productLevel === 'PROFESSIONAL' || productLevel === 'ENTERPRISE') && !isQuantifiableProduct;
  const isFreeProduct = productLevel === 'FREE';
  return {
    isQuantifiableProduct,
    productLevel,
    productLine,
    ownsProductLine,
    coreSeatTierOwned,
    isNonQuantifiableProOrEnterpriseProduct,
    isDowngrade,
    isFreeProduct
  };
};
export const useShowPricePerMonthPerSeatTB = ({
  skuId
}) => {
  const {
    isQuantifiableProduct,
    productLevel,
    ownsProductLine,
    coreSeatTierOwned
  } = useProductOwnershipDetails({
    skuId
  });
  if (isQuantifiableProduct) {
    return true;
  }
  const coreSeatTierOwnershipRule = coreSeatTierOwned === 'STARTER' && productLevel === 'STARTER';

  // If the highest owned tier is free or starter, show the price in "/mo/seat" format on ALL Starter cards
  if (coreSeatTierOwnershipRule) {
    return true;
  }

  // If looking at merch card of a hub OWNED, show price in "/mo/seat" format on Starter cards
  if (ownsProductLine && productLevel === 'STARTER') {
    return true;
  }
  return false;
};
export const useShowIncludedSeatsTB = ({
  skuId
}) => {
  const {
    productLevel,
    ownsProductLine,
    isNonQuantifiableProOrEnterpriseProduct
  } = useProductOwnershipDetails({
    skuId
  });
  const showPricePerMonthPerSeat = useShowPricePerMonthPerSeatTB({
    skuId
  });

  // Show "Includes Y seats" on ALL Non-quantifiable hubs' Pro/Ent card
  if (isNonQuantifiableProOrEnterpriseProduct) {
    return true;
  }
  const productLineOwnershipIncludedSeatsRule = productLevel === 'STARTER' && !ownsProductLine && !showPricePerMonthPerSeat;

  // Show "Includes Y seats" on Starter cards only if they DO NOT OWN productLine [and NOT in "/mo/seat" format]
  if (productLineOwnershipIncludedSeatsRule) {
    return true;
  }
  return false;
};
export const useAdditionalCoreSeatPriceTB = ({
  skuId
}) => {
  const isAppReady = useGlobalLoading();
  const {
    queryParamTerm
  } = useQueryParamTerm();
  // core seat product level owned or merch card's product level, whichever higher
  const productLevelForCoreSeats = useAppSelector(state => {
    if (!isAppReady || !skuId) {
      return null;
    }
    return productLevelForCoreSeatsSelector(state, {
      productConfigurations: [createProductConfig({
        skuId
      })]
    });
  });
  const coreSeatSku = useAppSelector(state => {
    if (!isAppReady || !productLevelForCoreSeats) {
      return null;
    }
    return coreSeatSkuForProductLevelSelector(state, {
      productLevel: productLevelForCoreSeats
    });
  });
  const coreSeatApiName = coreSeatSku === null || coreSeatSku === void 0 ? void 0 : coreSeatSku.productApiName;
  const {
    purchaseOptionsForTerm,
    loading
  } = usePurchaseableProductsForTerm({
    queryParamTerm,
    apiName: coreSeatApiName
  });
  const additionalCoreSeatsPrice = (purchaseOptionsForTerm === null || purchaseOptionsForTerm === void 0 ? void 0 : purchaseOptionsForTerm.monthly.calculatedPrice) || 0;
  return {
    additionalCoreSeatsPrice,
    loading
  };
};

// Show "Additional Seats start at $X/mo" for all non-quantifiable hubs' cards that are NOT downgrades and NOT in the "/mo/seat" format
export const useShowAdditionalSeatsPriceSubtextTB = ({
  skuId
}) => {
  const {
    isDowngrade,
    isFreeProduct
  } = useProductOwnershipDetails({
    skuId
  });
  const showPricePerMonthPerSeat = useShowPricePerMonthPerSeatTB({
    skuId
  });
  const {
    additionalCoreSeatsPrice
  } = useAdditionalCoreSeatPriceTB({
    skuId
  });
  const showAdditionalSeatsPriceSubtext = additionalCoreSeatsPrice > 0 && !isDowngrade && !showPricePerMonthPerSeat && !isFreeProduct;
  return showAdditionalSeatsPriceSubtext;
};